import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Kanban, CalendarDays, CheckSquare, PhoneCall, ClipboardList, BarChart3, ShieldCheck, Settings, UserCircle, Menu, X, LogOut } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { GlobalSearch } from "@/components/GlobalSearch";
import { NotificationBell } from "@/components/NotificationBell";

const NAV = [
  ["Dashboard", "/dashboard", LayoutDashboard], ["Leads", "/leads", Users], ["Pipeline", "/pipeline", Kanban],
  ["Meetings", "/meetings", CalendarDays], ["Tasks / Follow-ups", "/tasks", CheckSquare], ["Activities", "/activities", PhoneCall],
  ["EOD Report", "/eod", ClipboardList], ["Reports", "/reports", BarChart3], ["Team", "/team", ShieldCheck, true],
  ["Settings", "/settings", Settings], ["My Profile", "/profile", UserCircle],
];

const initials = (n = "") => n.split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

const Sidebar = ({ onNav }) => {
  const { isAdmin } = useAuth();
  return (
    <nav className="flex h-full flex-col border-r bg-white">
      <div className="flex h-16 items-center gap-2.5 border-b px-4">
        <img src="/logo-mark.png" alt="Realtytics" className="h-9 w-auto" data-testid="brand-logo" />
        <div>
          <p className="text-[15px] font-extrabold leading-none tracking-wide text-[hsl(var(--charcoal))]" style={{ fontFamily: "Montserrat" }}>REALTYTICS</p>
          <p className="mt-1 text-[9px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Sales CRM</p>
        </div>
      </div>
      <div className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {NAV.filter((n) => !n[3] || isAdmin).map(([label, to, Icon]) => (
          <NavLink key={to} to={to} onClick={onNav} data-testid={`nav-${to.slice(1)}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-[hsl(var(--sidebar-active))] font-semibold text-primary" : "text-[hsl(var(--sidebar-foreground))] hover:bg-slate-50 hover:text-primary"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </div>
      <p className="border-t px-5 py-3 text-[10px] text-muted-foreground">BMS · Intl · Ind · CRM</p>
    </nav>
  );
};

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 lg:block"><Sidebar /></aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64"><Sidebar onNav={() => setOpen(false)} /></aside>
        </div>
      )}
      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b bg-white/90 px-4 backdrop-blur-md sm:px-6">
          <button className="rounded-md p-2 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 hover:bg-slate-100">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">{initials(user?.name)}</span>
                  <span className="hidden text-left sm:block">
                    <span className="block text-sm font-semibold leading-tight">{user?.name}</span>
                    <span className="block text-[11px] text-muted-foreground">{isAdmin ? "Sales Admin" : "Sales Employee"}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs text-muted-foreground">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/profile")} data-testid="user-menu-profile">My Profile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings")}>Settings</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="user-menu-logout" onClick={async () => { await logout(); nav("/login"); }} className="text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
